/**
 * The render_chart tool result → something ChartCard can draw. The schema was already
 * validated server-side by Zod; this parser only guards against a malformed result so
 * the card can fall back to the raw JSON instead of a broken canvas.
 */

export type ChartSeries = {
  name: string;
  type: 'bar' | 'line';
  values: Array<number | null>;
};

export type ChartSpec = {
  title: string | null;
  labels: string[];
  series: ChartSeries[];
};

/**
 * Categorical palette in fixed slot order, per the dataviz rules: hues follow the
 * series index, never get cycled or generated, and both columns were validated with
 * the palette script against Shipdesk's own card surfaces (#f4f5f4 light, #21242a
 * dark) — lightness band, chroma floor, CVD separation and normal-vision floor all
 * pass. Light mode carries a contrast warning below 3:1 for some slots, which is why
 * ChartCard always offers the table view: that is the mandated relief, not a nicety.
 */
export const SERIES_COLORS_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'];
export const SERIES_COLORS_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300'];

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

export function parseChartSpec(text: string): ChartSpec | null {
  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch {
    return null;
  }

  const chart = asRecord(asRecord(payload)?.['chart']);
  const labels = chart?.['labels'];
  const series = chart?.['series'];
  if (!chart || !Array.isArray(labels) || !Array.isArray(series) || series.length === 0) {
    return null;
  }
  if (!labels.every((label) => typeof label === 'string')) {
    return null;
  }

  const parsedSeries: ChartSeries[] = [];
  for (const raw of series) {
    const record = asRecord(raw);
    const values = record?.['values'];
    if (
      !record ||
      typeof record['name'] !== 'string' ||
      !Array.isArray(values) ||
      values.length !== labels.length ||
      !values.every((value) => value === null || typeof value === 'number')
    ) {
      return null;
    }
    parsedSeries.push({
      name: record['name'],
      type: record['type'] === 'line' ? 'line' : 'bar',
      values: values as Array<number | null>,
    });
  }

  return {
    title: typeof chart['title'] === 'string' ? chart['title'] : null,
    labels: labels as string[],
    series: parsedSeries,
  };
}
